import { useInfiniteQuery } from "react-query";
import { Platform } from "react-native";
import useNetworkHealth from "./useNetworkHealth";
import { storeLocalData, getLocalData } from "../services/storage";
import openhouseAPI from "../services/openhouse";

export const useGetOpenHouses = (status: string, uuid: string) => {
  const network = useNetworkHealth();
  const perPage = Platform.OS === "web" ? 30 : 14;

  const queryKey = ["openHouses", status, uuid];

  const readCachedData = async () => {
    const cached = await getLocalData(`openHouses-${status}`);
    if (cached) {
      return cached;
    }

    return { openHouses: [] } as any;
  };

  const fetchOpenHouses = async ({ pageParam = 1 }) => {
    if (status === "cached") {
      const syncData = await getLocalData(`syncData`);
      if (syncData) {
        return syncData;
      }
      return [];
    }

    if (network.isOffline) {
      return readCachedData();
    }

    try {
      const res = await openhouseAPI.post("/openhouse/list", {
        status: status,
        page: pageParam,
        perPage: perPage,
        host_user_uuid: uuid,
      });

      if (pageParam === 1 && res?.data?.data) {
        await storeLocalData(`openHouses-${status}`, res?.data?.data);
      }

      return res?.data?.data;
    } catch (error) {
      const cachedData = await readCachedData();
      if (cachedData?.openHouses?.length) {
        return cachedData;
      }

      throw error;
    }
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetched,
    refetch,
    isLoading,
    isFetchingNextPage,
  } = useInfiniteQuery(queryKey, fetchOpenHouses, {
    getNextPageParam: (lastPage, allPages) => {
      // lastPage contains: { openHouses: [...], page: X, totalPages: Y, totalCount: Z }
      if (!lastPage) return undefined;

      const currentPage = lastPage.page || allPages.length;
      const totalPages = lastPage.totalPages;

      // If there are more pages to fetch, return the next page number
      if (totalPages && currentPage < totalPages) {
        return currentPage + 1;
      }

      return undefined;
    },

    staleTime: 30000,
    cacheTime: 30000,
    enabled: !!uuid, // Enable query only if uuid exists
    retry: 1, // Retry once if the query fails
  });

  const isWaitingForUuid = !uuid;

  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching: isWaitingForUuid ? true : isFetching,
    isFetched,
    refetch,
    isLoading: isWaitingForUuid ? true : isLoading,
    isFetchingNextPage,
  };
};
